import { createGtm, hasScript, loadScript } from '@gtm-support/vue-gtm';

const gtmId = import.meta.env.VITE_GTM_ID as string;
const gtmSource = import.meta.env.VITE_GTM_SOURCE as string;

/** Server-side container bundle served from our own GTM domain. */
const GTM_SERVER_SIDE_SCRIPT = '2gbsek14pe3o5jx.js';

/**
 * Loader URL for the container, or `undefined` to let `@gtm-support` fall back
 * to Google's own `gtm.js`.
 */
const source = gtmSource
  ? new URL(GTM_SERVER_SIDE_SCRIPT, gtmSource).href
  : undefined;

const gtmPlugin = (() => {
  if (!gtmId) {
    console.warn(
      'VITE_GTM_ID environment variable is not defined. GTM tracking will be disabled.',
    );
    return;
  }

  if (!gtmSource) {
    console.warn(
      'VITE_GTM_SOURCE environment variable is not defined. GTM tracking will use the default script issued by Google.',
    );
  }

  return createGtm({
    id: gtmId,
    source,
    enabled: true,
    // The container script is NOT injected on install — `loadGtmContainer()`
    // does it once the CMP has published its consent defaults. See below.
    loadScript: false,
    debug: import.meta.env.DEV,
  });
})();

/**
 * Injects the GTM container script.
 *
 * Deliberately split out of the plugin install (`loadScript: false`) because
 * Google Consent Mode is order-dependent: Didomi has to push its
 * `consent`/`default` values into the dataLayer **before** the container boots,
 * or every tag runs on GTM's implicit defaults instead of the visitor's actual
 * choices. `initCookieConsent` owns that handoff.
 *
 * The plugin stays `enabled: true` so `gtm.push()` calls made in the meantime
 * still land in `window.dataLayer`; GTM drains that queue when it boots, so
 * nothing tracked during the wait is lost.
 *
 * Idempotent — callers may race (CMP-ready callback vs. its timeout).
 */
export const loadGtmContainer = () => {
  if (!gtmPlugin || typeof window === 'undefined') return;
  if (hasScript(source)) return;

  loadScript(gtmId, { source, defer: false, compatibility: false });
};

export default gtmPlugin;
